/*
 * A dedicated API for interacting with EDS and EMS. Supports all CRUD operations and uses useSWR.
 */
import { useCallback, useState } from 'react'

import { parseErrorResponse } from '../utils/api/errorParser'
import { useKeycloak } from './keycloakContextProvider'

interface UseApiMutationInterface {
    url: string
    method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'
}

async function parseResponse<R>(response: Response): Promise<R> {
    const contentType = response.headers.get('Content-Type')
    const contentLength = response.headers.get('Content-Length')

    if (response.status === 204 || contentLength === '0') {
        return undefined as R
    }

    if (contentType?.includes('application/json')) {
        try {
            return (await response.json()) as R
        } catch {
            return (await response.text()) as R
        }
    }
    return (await response.text()) as R
}

export type MutationErrorResponse = {
    message: string
}
/**
 * A mutation hook used for POST, PUT, PATCH and DELETE requests. Use when you want to send data to server.
 * @param paramsObject
 */
export function useApiMutation<T, E = MutationErrorResponse, R = void>(paramsObject: UseApiMutationInterface) {
    const { tenant } = useKeycloak()
    const token = sessionStorage.getItem(`token-${tenant}`)
    const [isMutating, setIsMutating] = useState<boolean>(false)
    const { method, url } = paramsObject

    const mutate = useCallback(
        async (data: T, urlOverride?: string): Promise<R> => {
            try {
                setIsMutating(true)
                const response = await fetch(urlOverride ?? url, {
                    method,
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token && { Authorization: `Bearer ${token}`, 'X-Tenant-ID': `${tenant}` }),
                    },
                    body: JSON.stringify(data),
                })
                if (!response.ok) {
                    const errorMessage = await parseErrorResponse<E>(response)
                    throw new Error(errorMessage)
                }
                const responseData = parseResponse<R>(response)
                setIsMutating(false)
                return responseData
            } catch (err: unknown) {
                const errorMessage = err instanceof Error ? err.message : (err as string)
                setIsMutating(false)
                throw new Error(`Unable to process request.\nReason: ${errorMessage}`)
            }
        },
        [method, url, token]
    )
    return { mutate, isMutating }
}
